import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import {
  CAPACITY_LABELS,
  DECISION_META,
  followUpState,
  RECOMMENDATION_TYPE_LABELS,
  SIGNAL_LABELS,
  type CapacityBand,
  type HvBand,
  type HvDecision,
  type RecommendationType,
  type Signal,
} from '@hv/shared';
import { BAND_COLORS, BRAND, DECISION_COLORS, SIGNAL_COLORS } from '../theme';

export function Dot({ color, size = 8 }: { color: string; size?: number }) {
  return <Box component="span" aria-hidden sx={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />;
}

export function DecisionPill({ decision, label }: { decision: HvDecision; label?: string }) {
  const meta = DECISION_META[decision];
  const c = DECISION_COLORS[meta.color];
  return (
    <Box
      component="span"
      data-decision={decision}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25, borderRadius: 10, bgcolor: c.bg, color: c.fg, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
    >
      <Dot color={c.dot} />
      {label ? `${label}: ` : ''}
      {meta.label}
    </Box>
  );
}

export function SignalChip({ signal }: { signal: Signal }) {
  const c = SIGNAL_COLORS[signal];
  return <Chip size="small" label={SIGNAL_LABELS[signal]} sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 600, height: 22, fontSize: 11 }} />;
}

export function SignalChips({ signals }: { signals: Signal[] }) {
  if (!signals.length) return <Typography variant="caption" color="text.secondary">—</Typography>;
  return (
    <Stack direction="row" gap={0.5} flexWrap="wrap">
      {signals.map((s) => (
        <SignalChip key={s} signal={s} />
      ))}
    </Stack>
  );
}

export function RecTypeTag({ type }: { type: RecommendationType }) {
  return <Chip size="small" variant="outlined" label={RECOMMENDATION_TYPE_LABELS[type]} sx={{ height: 20, fontSize: 11, color: BRAND.muted }} />;
}

export function ScoreBar({
  score,
  band,
  overridden,
  status,
  aiScore,
}: {
  score: number | null;
  band: HvBand;
  overridden: boolean;
  status: 'succeeded' | 'failed' | 'pending' | 'none';
  aiScore: number | null;
}) {
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Box sx={{ width: 54, height: 6, borderRadius: 4, bgcolor: '#eceff3', overflow: 'hidden' }} aria-hidden>
        <Box sx={{ width: `${score ?? 0}%`, height: '100%', bgcolor: BAND_COLORS[band] }} />
      </Box>
      <Typography component="span" sx={{ fontWeight: 700, fontSize: 14, minWidth: 22 }} data-testid="hv-score">
        {score ?? '—'}
      </Typography>
      {overridden && (
        <Tooltip title={`Staff override · Loom score ${aiScore ?? '—'}`}>
          <Box component="span" sx={{ fontSize: 10, fontWeight: 700, color: '#6b46c1', bgcolor: '#efe9ff', borderRadius: 1, px: 0.6 }}>
            override
          </Box>
        </Tooltip>
      )}
      {status === 'pending' && (
        <Tooltip title="Scoring queued">
          <HourglassTopIcon fontSize="inherit" aria-label="Scoring queued" sx={{ color: BRAND.muted }} />
        </Tooltip>
      )}
      {status === 'failed' && (
        <Tooltip title="Last scoring attempt failed">
          <ErrorOutlineIcon fontSize="inherit" aria-label="Last scoring attempt failed" color="error" />
        </Tooltip>
      )}
    </Stack>
  );
}

export function ScoreRing({ score, band }: { score: number | null; band: HvBand }) {
  return (
    <Box
      role="img"
      aria-label={`Impact Potential ${score ?? 'not scored'}`}
      data-testid="score-ring"
      sx={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        bgcolor: BAND_COLORS[band],
        color: band === 'unscored' || band === 'low' ? BRAND.ink : '#fff',
        fontWeight: 800,
        fontSize: 24,
      }}
    >
      {score ?? '—'}
    </Box>
  );
}

const FOLLOWUP_STYLE = {
  overdue: { bg: '#fbe9e8', fg: '#9e322e', label: 'Overdue' },
  soon: { bg: '#fdf4dc', fg: '#7a5a06', label: 'Due soon' },
  later: { bg: '#eaf1fb', fg: '#2c5a96', label: 'Scheduled' },
  none: { bg: '#f0f2f5', fg: BRAND.muted, label: 'No follow-up' },
};

export function FollowUpPill({ date }: { date: string | null }) {
  const state = followUpState(date);
  const s = FOLLOWUP_STYLE[state];
  return (
    <Box component="span" data-followup={state} sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 10, bgcolor: s.bg, color: s.fg, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {s.label}
      {date ? ` · ${date}` : ''}
    </Box>
  );
}

export function CapacityLabel({ band }: { band: CapacityBand }) {
  return <span>{CAPACITY_LABELS[band]}</span>;
}
